import { __assign, __extends, __rest } from "tslib";
import React from 'react';
import mergeWith from 'lodash/mergeWith';
import classnames from 'classnames';
import { EditorState, ContentState, convertToRaw, TOOLBARS, } from 'wix-rich-content-editor-common';
import { RichContentEditor, RichContentEditorModal, } from 'wix-rich-content-editor';
import { getPlugins, modalsMap } from '../plugins';
import { FeatureExtractor } from '../features/Extractor';
import Entry from './Mention/Entry';
import ToolbarWrapper from './ToolbarWrapper';
import { matchMentionsAndAttachEntities } from '../plugins/mention/modifiers';
import { InlineToolbarButtons } from '../enums';
import baseTheme from '../theme';
import { attachImage } from '../plugins/plugin-image/modifiers';
import { addText, markEmptyAtomicAdjacentAndFocused, getCurrentSelectionLength, fixAtomicsBlockingEditing, } from '../utils';
import ExternalToolbar from './ExternalToolbar/ExternalToolbar';
import SideToolbar from './SideToolbar/SideToolbar';
var noop = function () {
    // noop
};
var DaEditor = /** @class */ (function (_super) {
    __extends(DaEditor, _super);
    function DaEditor(props) {
        var _this = _super.call(this, props) || this;
        _this.state = {
            initialContent: _this.props.initialContent,
            isMounted: false,
            mentions: [],
            isLocked: false,
        };
        _this.wasPaste = false;
        _this.fixMentions = false;
        _this.onChange = function (editorState) {
            var nextState = editorState;
            var _a = _this.props, filterPaste = _a.filterPaste, plugins = _a.plugins, onContentChange = _a.onContentChange;
            // Paste handling, consumer provides function that cleans the state
            if (filterPaste && _this.wasPaste) {
                _this.wasPaste = false;
                nextState = filterPaste(nextState);
            }
            // Empty block UX++
            nextState = fixAtomicsBlockingEditing(nextState);
            markEmptyAtomicAdjacentAndFocused(nextState);
            var currentContent = nextState.getCurrentContent();
            // Mentions repair (fixes mentions that are not selected via dropdown)
            if (plugins && plugins['Mention'] && _this.fixMentions) {
                _this.fixMentions = false;
                currentContent = matchMentionsAndAttachEntities(currentContent);
                nextState = EditorState.set(nextState, {
                    currentContent: currentContent,
                });
            }
            var rawContentState = convertToRaw(currentContent);
            var featuresData = _this.featureExtractor.extract(nextState, rawContentState);
            onContentChange &&
                onContentChange(rawContentState, currentContent, featuresData, nextState.getSelection());
            _this.setState({
                editorState: nextState,
            });
        };
        _this.clearState = function (triggerChange) {
            if (triggerChange === void 0) { triggerChange = false; }
            var onContentChange = _this.props.onContentChange;
            var emptyContent = ContentState.createFromText('');
            var rawContentState = convertToRaw(emptyContent);
            if (!_this.state.editorState) {
                return;
            }
            // To avoid an issue where the cursor jumps to the beginning of the text we
            // have to do all of this juggling in order to clear the state.
            // https://github.com/facebook/draft-js/issues/410
            var nextState = EditorState.moveFocusToEnd(EditorState.push(_this.state.editorState, emptyContent, 'remove-range'));
            _this.setState({ editorState: nextState });
            if (triggerChange && onContentChange) {
                onContentChange(rawContentState, emptyContent, _this.featureExtractor.extract(nextState, rawContentState), nextState.getSelection());
            }
        };
        _this.setEditorRef = function (ref) {
            _this.editor = ref;
            if (!_this.editor) {
                return;
            }
            _this.editorToolbarProps = _this.editor.getToolbarProps(TOOLBARS.INSERT_PLUGIN);
            _this.props.onEditorInit &&
                _this.props.onEditorInit(_this.getEditorToolbars(), _this.getEditorAPI());
        };
        _this.getCurrentSelection = function () {
            var editorState = _this.state.editorState;
            return editorState && editorState.getSelection();
        };
        _this.setSelection = function (selectionState) {
            var editorState = _this.state.editorState;
            if (!editorState) {
                return;
            }
            var currentSectionState = editorState.getSelection();
            var newEditorState = EditorState.forceSelection(editorState, currentSectionState.merge(selectionState));
            _this.setState({
                editorState: newEditorState,
            });
        };
        _this.moveFocusToEnd = function () {
            var editorState = _this.state.editorState;
            if (!editorState) {
                return;
            }
            var newEditorState = EditorState.moveFocusToEnd(editorState);
            _this.setState({
                editorState: newEditorState,
            });
        };
        _this.retrieveToolbar = function (Toolbar) { return function (props) { return (React.createElement(ToolbarWrapper, null,
            React.createElement(Toolbar, __assign({}, props)))); }; };
        _this.setMentions = function (mentions) {
            _this.setState({ mentions: mentions });
        };
        _this.onMentionRequested = function (value) {
            _this.props.onMentionRequested &&
                _this.props.onMentionRequested(value, _this.setMentions);
        };
        _this.getToolbarSettings = function (_a) {
            var pluginButtons = _a.pluginButtons, textButtons = _a.textButtons, pluginTextButtons = _a.pluginTextButtons, pluginButtonProps = _a.pluginButtonProps;
            var _b = _this.props, hideSideToolbar = _b.hideSideToolbar, inlineToolbarButtons = _b.inlineToolbarButtons, inlineToolbarDecoratorFn = _b.inlineToolbarDecoratorFn;
            var defaultSideToolbarVisibilityFn = function (editorState) {
                var selection = editorState.getSelection();
                var currentContent = editorState.getCurrentContent();
                var currentBlock = currentContent.getBlockForKey(selection.getStartKey());
                return currentBlock.getLength() === 0;
            };
            var externalButtons = [];
            if (pluginButtonProps) {
                externalButtons = pluginButtonProps
                    .filter(function (_a) {
                    var toolbars = _a.toolbars;
                    return toolbars && toolbars.includes(TOOLBARS.INSERT_PLUGIN);
                })
                    .map(function (_a) {
                    var name = _a.name;
                    return name;
                });
                // Giphy duped and I cant find why...
                externalButtons = externalButtons.filter(function (name, idx) { return externalButtons.indexOf(name) === idx; });
            }
            var SideWrapper = function (props) {
                if (!_this.editorToolbarProps) {
                    return null;
                }
                return React.createElement(SideToolbar, __assign({ toolbarProps: _this.editorToolbarProps }, props));
            };
            return [
                {
                    name: TOOLBARS.INLINE,
                    getButtons: function () {
                        return {
                            desktop: inlineToolbarButtons,
                            mobile: {
                                ios: inlineToolbarButtons,
                                android: inlineToolbarButtons,
                            },
                        };
                    },
                    getToolbarDecorationFn: inlineToolbarDecoratorFn,
                },
                {
                    name: TOOLBARS.SIDE,
                    getVisibilityFn: function () { return ({
                        desktop: hideSideToolbar
                            ? function () { return false; }
                            : defaultSideToolbarVisibilityFn,
                        mobile: {
                            ios: function () { return true; },
                            android: function () { return true; },
                        },
                    }); },
                    getToolbarDecorationFn: function () { return ({
                        desktop: function () { return SideWrapper; },
                    }); },
                },
                {
                    name: TOOLBARS.MOBILE,
                    shouldCreate: function () { return ({
                        mobile: {
                            ios: true,
                            android: true,
                        },
                    }); },
                },
                {
                    name: TOOLBARS.FOOTER,
                    getVisibilityFn: function () { return ({
                        desktop: function () { return false; },
                        mobile: {
                            ios: function () { return false; },
                            android: function () { return false; },
                        },
                    }); },
                    shouldCreate: function () { return ({
                        desktop: function () { return false; },
                        mobile: {
                            ios: function () { return false; },
                            android: function () { return false; },
                        },
                    }); },
                },
                {
                    name: TOOLBARS.INSERT_PLUGIN,
                    getVisibilityFn: function () { return ({
                        desktop: function () { return true; },
                        mobile: {
                            ios: function () { return true; },
                            android: function () { return true; },
                        },
                    }); },
                    shouldCreate: function () { return ({
                        desktop: function () { return true; },
                        mobile: {
                            ios: function () { return true; },
                            android: function () { return true; },
                        },
                    }); },
                    getButtons: function () { return ({
                        desktop: externalButtons,
                        mobile: {
                            ios: externalButtons,
                            android: externalButtons,
                        },
                    }); },
                },
            ];
        };
        _this.handlePastedText = function (text, html, editorState) {
            // External Paste cleanup (cleans word etc and allows internal pasting to work)
            // We do not clean here because modifying the state means you have to fully
            // handle the paste so we mark paste as done and then filter in onChange
            _this.wasPaste = true;
            var _a = _this.props, charLimit = _a.charLimit, deviationPasteValidator = _a.deviationPasteValidator, onDeviationPasted = _a.onDeviationPasted, plugins = _a.plugins;
            // Deviation paste support
            if (plugins &&
                plugins['Image'] &&
                deviationPasteValidator &&
                onDeviationPasted) {
                var deviationLookup = deviationPasteValidator(text);
                if (deviationLookup) {
                    onDeviationPasted(deviationLookup, function (deviation) {
                        if (deviation) {
                            _this.setState({
                                isLocked: false,
                            }, function () {
                                _this.onChange(attachImage(deviation, editorState));
                            });
                        }
                        else {
                            _this.setState({
                                isLocked: false,
                            }, function () {
                                _this.onChange(addText(text, editorState));
                            });
                        }
                    });
                    _this.setState({ isLocked: true });
                    return 'handled';
                }
            }
            // Char limit handling
            if (charLimit) {
                var currentContent = editorState.getCurrentContent();
                var currentContentLength = currentContent.getPlainText('').length;
                var selectedTextLength = getCurrentSelectionLength(editorState);
                if (currentContentLength + text.length - selectedTextLength > charLimit) {
                    return 'handled';
                }
            }
        };
        _this.handleBeforeInput = function (chars, editorState, eventTimeStamp) {
            if (chars === ' ') {
                _this.fixMentions = true;
            }
            if (!_this.props.charLimit) {
                return;
            }
            // Char limit handling
            if (_this.state.editorState) {
                var currentContent = _this.state.editorState.getCurrentContent();
                var currentContentLength = currentContent.getPlainText('').length || 0;
                var selectedTextLength = getCurrentSelectionLength(_this.state.editorState);
                if (currentContentLength - selectedTextLength >
                    _this.props.charLimit - 1) {
                    return 'handled';
                }
            }
        };
        _this.onFocus = function (event) {
            _this.setState({ isFocused: true });
            _this.props.onFocus && _this.props.onFocus(event);
        };
        _this.onBlur = function (event) {
            _this.setState({ isFocused: false });
            _this.props.onBlur && _this.props.onBlur(event);
        };
        _this.featureExtractor = new FeatureExtractor();
        _this.theme = mergeWith(__assign({}, baseTheme), props.theme || {}, function (defaultStyleClassName, themeClassName) {
            return classnames(defaultStyleClassName, themeClassName);
        });
        _this.setup(props);
        return _this;
    }
    DaEditor.prototype.setup = function (props) {
        var _this = this;
        var onImageRequested = props.onImageRequested, onColorPickerRequested = props.onColorPickerRequested, plugins = props.plugins, features = props.features, onModalClose = props.onModalClose, featureFlags = props.featureFlags, scrollableRef = props.scrollableRef;
        // Features
        this.featureExtractor.register(features);
        // Editor API
        this.helpers = {
            featureFlags: featureFlags,
            handleFileSelection: function (index, multiple, updateEntity, deleteBlock, componentData) {
                onImageRequested &&
                    onImageRequested(multiple, updateEntity, componentData);
            },
            openModal: function (data) {
                var modalStyles = data.modalStyles, modalProps = __rest(data, ["modalStyles"]);
                _this.onModalOpen(modalProps, modalStyles);
            },
            closeModal: function () {
                onModalClose && onModalClose();
            },
            onVideoSelected: function () { },
            openColorPicker: function (pickerProps, onPickerDone) {
                var done = function () {
                    _this.editor.focus();
                    onPickerDone();
                };
                onColorPickerRequested && onColorPickerRequested(done, pickerProps);
            },
            scrollableRef: scrollableRef,
        };
        var _a = getPlugins(plugins, featureFlags), pluginsCreated = _a.plugins, extras = _a.extras;
        this.plugins = pluginsCreated;
        this.extras = extras;
    };
    DaEditor.prototype.componentDidCatch = function () {
        this.setState({
            initialContent: this.state.editorState &&
                convertToRaw(this.state.editorState.getCurrentContent()),
            editorState: undefined,
        });
    };
    DaEditor.prototype.componentDidMount = function () {
        window['richContentHideTooltips'] = true;
        this.setState({ isMounted: true });
    };
    DaEditor.prototype.onModalOpen = function (modalProps, modalStyles) {
        this.props.onModalOpen &&
            this.props.onModalOpen(function () { return React.createElement(RichContentEditorModal, __assign({ modalsMap: modalsMap }, modalProps)); }, modalProps, modalStyles);
    };
    DaEditor.prototype.getEditorAPI = function () {
        return {
            clearEditor: this.clearState,
            focusEditor: this.editor.focus,
            blurEditor: this.editor.blur,
            getCurrentSelection: this.getCurrentSelection,
            setSelection: this.setSelection,
            moveFocusToEnd: this.moveFocusToEnd,
        };
    };
    DaEditor.prototype.getEditorToolbars = function () {
        var _this = this;
        var retrievedToolbars = {};
        retrievedToolbars[TOOLBARS.INSERT_PLUGIN] = this.retrieveToolbar(function (props) { return (React.createElement(ExternalToolbar, __assign({}, _this.editorToolbarProps, props))); });
        // Hack we reach into internals here to get side toolbar
        retrievedToolbars[TOOLBARS.SIDE] = this.editor.editor.toolbars[TOOLBARS.SIDE].SideToolbar;
        return retrievedToolbars;
    };
    DaEditor.prototype.getThemeClassName = function () {
        var isDarkTheme = this.props.isDarkTheme;
        return "da-editor-theme-" + (isDarkTheme ? 'dark' : 'bright');
    };
    DaEditor.prototype.getWrapperProps = function () {
        var _a;
        var _b = this.props, hideSideToolbar = _b.hideSideToolbar, fixedParagraph = _b.fixedParagraph, showMediaAlwaysSmall = _b.showMediaAlwaysSmall, isMobile = _b.isMobile;
        var isLocked = this.state.isLocked;
        var wrapperProps = {};
        if (hideSideToolbar) {
            wrapperProps['data-daeditor-hide-side-toolbar'] = true;
        }
        if (fixedParagraph) {
            wrapperProps['data-daeditor-fixed-paragraph'] = true;
        }
        if (showMediaAlwaysSmall) {
            wrapperProps['data-daeditor-small-media'] = true;
        }
        if (isMobile) {
            wrapperProps['data-daeditor-hide-inline-toolbar'] = true;
        }
        if (isLocked) {
            wrapperProps['data-daeditor-locked'] = true;
        }
        wrapperProps.className = classnames('da-editor', this.getThemeClassName(), this.state.isFocused && 'da-editor-focused', this.props.className, (_a = {},
            _a[this.theme.mobile] = isMobile,
            _a));
        return wrapperProps;
    };
    // https://github.com/wix-incubator/rich-content/blob/master/docs/UiSettings.md
    DaEditor.prototype.getUiSettings = function () {
        return {
            linkPanel: {
                blankTargetToggleVisibilityFn: function () { return false; },
                nofollowRelToggleVisibilityFn: function () { return false; },
                placeholder: 'https://www.deviantart.com/',
            },
        };
    };
    DaEditor.prototype.render = function () {
        var _a = this.state, isMounted = _a.isMounted, editorState = _a.editorState, initialContent = _a.initialContent, isLocked = _a.isLocked;
        if (!isMounted) {
            return null;
        }
        var _b = this.props, locale = _b.locale, localeResource = _b.localeResource, isMobile = _b.isMobile, placeholder = _b.placeholder, tabIndex = _b.tabIndex, handleReturn = _b.handleReturn, showMediaAlwaysSmall = _b.showMediaAlwaysSmall, editorKey = _b.editorKey, stripPastedStyles = _b.stripPastedStyles, pluginsConfig = _b.plugins;
        var MentionSuggestions = this.extras.MentionSuggestions;
        return (React.createElement("div", __assign({}, this.getWrapperProps()),
            React.createElement(RichContentEditor, { readOnly: isLocked, editorKey: editorKey, ref: this.setEditorRef, onChange: this.onChange, helpers: this.helpers, plugins: this.plugins, relValue: "nofollow", anchorTarget: "_blank", initialState: initialContent, editorState: editorState, isMobile: isMobile, config: __assign(__assign({}, pluginsConfig), { getToolbarSettings: this.getToolbarSettings, image: { showMediaAlwaysSmall: showMediaAlwaysSmall }, gallery: { showMediaAlwaysSmall: showMediaAlwaysSmall }, uiSettings: this.getUiSettings() }), locale: locale, localeResource: localeResource, theme: this.theme, placeholder: placeholder, tabIndex: tabIndex, onFocus: this.onFocus, onBlur: this.onBlur, handleReturn: handleReturn, handlePastedText: this.handlePastedText, handleBeforeInput: this.handleBeforeInput, stripPastedStyles: stripPastedStyles }),
            MentionSuggestions && (React.createElement(MentionSuggestions, { key: editorKey, onSearchChange: this.onMentionRequested, suggestions: this.state.mentions, entryComponent: Entry }))));
    };
    DaEditor.defaultProps = {
        onMentionRequested: noop,
        onImageRequested: noop,
        onEmotesRequested: noop,
        onColorPickerRequested: noop,
        onEditorInit: noop,
        onModalOpen: noop,
        onModalClose: noop,
        locale: 'en',
        isDarkTheme: false,
        hideSideToolbar: false,
        fixedParagraph: false,
        showMediaAlwaysSmall: false,
        features: [],
        inlineToolbarButtons: [
            InlineToolbarButtons.BOLD,
            InlineToolbarButtons.ITALIC,
            InlineToolbarButtons.UNDERLINE,
            InlineToolbarButtons.TEXT_TOOLS_COLOR,
            InlineToolbarButtons.LINK,
            InlineToolbarButtons.SEPARATOR,
            InlineToolbarButtons.TITLE,
            InlineToolbarButtons.ORDERED_LIST,
            InlineToolbarButtons.UNORDERED_LIST,
            InlineToolbarButtons.BLOCKQUOTE,
        ],
    };
    return DaEditor;
}(React.PureComponent));
export default DaEditor;
//# sourceMappingURL=Editor.js.map